import { forwardRef, useRef } from 'react';
import { createPortal } from 'react-dom';

import { Box, IconButton, IconButtonProps, Tooltip } from '@mui/material';

import { styles } from './IconTooltipButton.styles';

export type TIconTooltipButtonProps = Omit<IconButtonProps, 'aria-label'> & {
  /** Already-translated text. It is the tooltip title and the button's accessible name. */
  label: string;
  /** Already-translated text shown instead of `label` (tooltip and name) while `disabled`. */
  disabledLabel?: string;
  /**
   * Optional longer screen-reader name (for example "Clear Filter: Status"). It overrides the
   * accessible name only. The tooltip still shows `label` / `disabledLabel`.
   */
  'aria-label'?: string;
};

/**
 * Icon button with a tooltip. Hover, keyboard focus or a tap shows the tooltip.
 *
 * Why: one shared pattern, so every icon-only button has a tooltip that matches its screen-reader
 * name. The tooltip look and zoom handling are copied from TitleWithInfo. The existing tooltips
 * (TitleWithInfo, NavListItem) are untouched.
 *
 * Constraints:
 * - One `label` is both the tooltip text and the `aria-label`. Use `aria-label` to give screen
 *   readers a longer name without changing the tooltip. `describeChild` keeps the tooltip as the
 *   button's description, not its name.
 * - Placement under the app-wide html zoom (APP_ZOOM, src/index.tsx): MUI Popper measures in
 *   zoomed px but writes CSS px, so a tooltip portaled to <body> lands off the icon. The tooltip
 *   is portaled into an empty counter-zoom span and the tooltip box re-applies the zoom.
 *   `strategy: 'fixed'` stops `overflow: hidden` parents from clipping it. No arrow, for the
 *   same zoom reason.
 * - That counter-zoom span is itself portaled to <body> (absolute, 0x0, no layout space). A fixed
 *   popper is placed relative to the nearest ancestor with a `transform`, and the span would
 *   otherwise sit inside one (GenericModal centres itself with `translate(-50%, -50%)`; MUI
 *   Drawer/Popover/Dialog transitions also transform), which shifted the tooltip away from the
 *   icon. Under <body> the only containing block is the viewport, in every context. <body> is
 *   inside the zoomed <html>, so the counter-zoom stays correct.
 * - Touch: opens on touch start with no delay and closes after 3 seconds.
 * - Disabled: a disabled button fires no hover events, so it is wrapped in an inline-flex span
 *   that carries the tooltip. A disabled button is not focusable, so keyboard users do not get
 *   the tooltip then. That is accepted. `disabledLabel` replaces `label` while disabled.
 * - An empty shown label renders the bare IconButton with no Tooltip.
 * - `size` and all other IconButton props pass through unchanged. No default size is imposed.
 * - Do not add page-specific rules here. Callers own their sizes, colours and positions.
 */
export const IconTooltipButton = forwardRef<HTMLButtonElement, TIconTooltipButtonProps>(
  ({ label, disabledLabel, children, disabled, 'aria-label': ariaLabel, ...rest }, ref) => {
    // Holds the counter-zoom span (under <body>) that MUI portals the tooltip into.
    const tooltipContainerRef = useRef<HTMLSpanElement>(null);
    const shownLabel = disabled && disabledLabel ? disabledLabel : label;

    const button = (
      <IconButton ref={ref} {...rest} disabled={disabled} aria-label={ariaLabel ?? shownLabel}>
        {children}
      </IconButton>
    );

    if (!shownLabel) {
      return button;
    }

    return (
      <>
        <Tooltip
          title={shownLabel}
          placement="bottom"
          describeChild
          enterDelay={200}
          enterTouchDelay={0}
          leaveTouchDelay={3000}
          PopperProps={{
            container: () => tooltipContainerRef.current,
            popperOptions: { strategy: 'fixed' },
            sx: styles.tooltipPopper,
          }}
        >
          {disabled ? (
            <Box component="span" sx={styles.disabledWrapper}>
              {button}
            </Box>
          ) : (
            button
          )}
        </Tooltip>
        {createPortal(
          <Box
            component="span"
            ref={tooltipContainerRef}
            sx={styles.tooltipContainer}
            data-testid="icon-tooltip-container"
          />,
          document.body,
        )}
      </>
    );
  },
);

IconTooltipButton.displayName = 'IconTooltipButton';
